/**
 * nextifycrm.js — the dependency-free embed loader.
 *
 * This is the single highest-RPS, most donor-visible artefact we ship: it runs
 * on every page of every partner's website, on devices we do not control, next
 * to scripts we did not write. Three constraints are permanent:
 *
 *   1. ≤8KB gzipped, no dependencies. Enforced by scripts/check-size.mjs, which
 *      fails the build. docs/UX-CHECKOUT.md §1.
 *   2. It NEVER loads the checkout. It parses markup, paints chrome from cached
 *      config, and registers handlers. The modal is fetched on intent (hover /
 *      touchstart / viewport dwell) and opened on click. That deferral is most
 *      of the ~1.7s benchmark.
 *   3. It never throws into the host page. A broken donate button is bad; a
 *      broken donate button that also breaks the partner's site is a phone call
 *      on a Friday night.
 *   4. It writes no bookkeeping attributes onto markup the host page authored.
 *      Partner sites are server-rendered React/Vue/Svelte more often than not,
 *      and this script is in <head> — so it mounts BEFORE the framework
 *      hydrates. Every attribute we stamp on a host element in that window is
 *      an attribute the framework's server HTML did not have, which it reports
 *      as a hydration mismatch. Loader state lives in JS (see `mountedHosts`);
 *      only nodes the loader itself created get loader attributes.
 *
 * It paints useful chrome from the edge config, warms the hosted checkout on
 * intent, and owns the modal/history/focus boundary. Stripe remains isolated in
 * the hosted `/give` document; this loader never touches payment details.
 */

/*
  A NOTE ON THE HEX LITERALS BELOW.

  This loader cannot import `@nextifycrm/ui`: `tokens.ts` would drag a whole
  module into a bundle that ships alone on partner pages against an 8KB budget.
  So it is one of the "places CSS cannot reach" that `packages/ui/src/tokens.ts`
  documents, and it mirrors four palette values by hand. Each one carries the
  token it mirrors in a comment beside it, and
  `apps/web/test/palette-parity.test.ts` asserts those four against the parsed
  `theme.css` — because the Nextify Labs rebrand moved the palette and left this
  file painting the retired teal-and-warm-paper one on every partner site.

  The rgba() scrims, shadows and white overlays are NOT tokens and must not
  become them: they are compositing values for chrome that floats over a host
  page we do not control. The test names the four deliberately, so a future
  rebrand fails on the values that must track and stays silent about these.
*/

/** Bumped when the config CONTRACT changes, not when this file changes. */
const CONFIG_API_VERSION = 'v1';

/** Where the loader was served from — the config origin, unless overridden. */
const DEFAULT_ORIGIN = 'https://nextifycrm.org';

type ElementKind = 'button' | 'preset-amounts' | 'form' | 'goal-meter' | 'link';

interface ElementConfigRef {
  /** The element CODE from the dashboard. Configuration lives server-side. */
  code: string;
  kind: ElementKind;
  /** Deep-link overrides the host page may set on the tag itself. */
  amount: string | null;
  fund: string | null;
  campaign: string | null;
  /** One anonymous measurement session per mounted DOM instance. */
  sessionId: string;
  /** Server-signed source/UTM snapshot; never treated as payment authority. */
  attributionToken?: string;
}

interface LoaderOptions {
  origin: string;
  /**
   * Splits the config-fetch cache key (`?test=1`) and nothing else. LIVE vs
   * TEST for the checkout itself — which Stripe key pair the donor's payment
   * runs on — is decided on the server per request (`server/mode.ts`, demo
   * tenants pinned to test in `server/demo-tenant.ts`) and cannot be set from
   * partner markup: an attribute any host page can type must never choose
   * whether real money moves.
   */
  testMode: boolean;
  debug: boolean;
}

interface PublicConfig {
  elementCode: string;
  elementType?: string;
  defaultFundId: string;
  funds: Array<{ id: string; label: string; zakatMode: boolean }>;
  presets: { default: number[]; zakat: number[] };
  appearance: { color: string | null; label: string | null };
  campaign?: { name: string; goalCents: number | null; endsOn: string | null } | null;
  progress?: {
    raisedCents: number;
    goalCents: number | null;
    donorCount: number;
    giftCount: number;
    campaignName: string | null;
    endsOn: string | null;
    state: 'live' | 'goal_met' | 'closed';
  } | null;
}

/** Which host elements are already mounted. A WeakSet, not a data attribute,
 *  so the loader leaves no trace on host markup — see constraint 4. */
const mountedHosts = new WeakSet<HTMLElement>();
const warmed = new Map<string, HTMLIFrameElement>();
let activeModal: HTMLElement | null = null;
let activeTrigger: HTMLElement | null = null;
let activeCheckoutOrigin: string | null = null;
let activeCheckoutWindow: Window | null = null;
let activeFunnelRef: ElementConfigRef | null = null;
let activeFunnelOptions: LoaderOptions | null = null;
let modalOwnsHistoryEntry = false;
let lockedScrollY = 0;
let inertedByLoader: HTMLElement[] = [];
let savedBodyStyle: { position: string; top: string; width: string } | null = null;

function warn(message: string, detail?: unknown): void {
  // Never throw into the host page. See constraint 3.
  if (typeof console !== 'undefined') console.warn(`[nextifycrm] ${message}`, detail ?? '');
}

/**
 * The <script> tag that loaded us, for origin and options. `document.currentScript`
 * is null inside a module or when the bundle is re-executed, so fall back to a
 * query by src.
 */
function loaderScript(): HTMLScriptElement | null {
  const current = document.currentScript;
  if (current && current instanceof HTMLScriptElement) return current;
  const all = document.getElementsByTagName('script');
  for (let i = all.length - 1; i >= 0; i--) {
    const el = all[i];
    // `.ts` keeps the source entry usable by the local Vite harness.
    if (el && /nextifycrm(\.min)?\.(js|ts)(\?|$)/.test(el.src)) return el;
  }
  return null;
}

function readOptions(): LoaderOptions {
  const script = loaderScript();
  const data = script?.dataset ?? ({} as DOMStringMap);
  let origin = data['nextifycrmOrigin'] ?? '';
  if (!origin && script?.src) {
    try {
      origin = new URL(script.src, document.baseURI).origin;
    } catch {
      /* malformed src — fall through to the default */
    }
  }
  return {
    origin: origin || DEFAULT_ORIGIN,
    testMode: data['nextifycrmTestMode'] === 'true',
    debug: data['nextifycrmDebug'] === 'true',
  };
}

/** Which element the markup is asking for. docs/UX-CHECKOUT.md §1.1. */
function kindOf(el: HTMLElement): ElementKind {
  if (el.hasAttribute('data-preset-amounts')) return 'preset-amounts';
  if (el.hasAttribute('data-form')) return 'form';
  if (el.hasAttribute('data-goal-meter')) return 'goal-meter';
  if (el.tagName === 'A') return 'link';
  return 'button';
}

/** `#nextifycrm/CODE` — the Donate Link form of the contract. */
function codeFromHash(href: string): string | null {
  const match = /#nextifycrm\/([\w-]+)/.exec(href);
  return match?.[1] ?? null;
}

function parseElement(el: HTMLElement): ElementConfigRef | null {
  const code = el.dataset['nextifycrm'] || codeFromHash(el.getAttribute('href') ?? '');
  if (!code) return null;
  return {
    code,
    kind: kindOf(el),
    amount: el.dataset['nextifycrmAmount'] ?? null,
    fund: el.dataset['nextifycrmFund'] ?? null,
    campaign: el.dataset['nextifycrmCampaign'] ?? null,
    sessionId: newSessionId(),
  };
}

function newSessionId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function reportFunnel(
  ref: ElementConfigRef,
  options: LoaderOptions,
  eventName: 'element_view' | 'checkout_open' | 'checkout_closed',
): void {
  const url = new URL(
    `/${CONFIG_API_VERSION}/elements/${encodeURIComponent(ref.code)}/funnel`,
    options.origin,
  );
  const page = new URL(window.location.href);
  const utm = {
    source: page.searchParams.get('utm_source') ?? undefined,
    medium: page.searchParams.get('utm_medium') ?? undefined,
    campaign: page.searchParams.get('utm_campaign') ?? undefined,
    term: page.searchParams.get('utm_term') ?? undefined,
    content: page.searchParams.get('utm_content') ?? undefined,
  };
  void fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    keepalive: true,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ sessionId: ref.sessionId, eventName, utm }),
  })
    .then(async (response) => {
      if (!response.ok) return;
      const body = (await response.json()) as { attributionToken?: unknown };
      if (typeof body.attributionToken !== 'string') return;
      ref.attributionToken = body.attributionToken;
      if (activeFunnelRef === ref && activeCheckoutWindow && activeCheckoutOrigin) {
        activeCheckoutWindow.postMessage(
          { type: 'nextifycrm:attribution', token: body.attributionToken },
          activeCheckoutOrigin,
        );
      }
    })
    .catch(() => {
      // Analytics must never make an element or checkout fail.
    });
}

/**
 * Where this element's config comes from.
 *
 * Served from KV at the edge with a D1 origin fallback — KV is a pure cache and
 * a miss must never render as "element not found" (ARCHITECTURE.md §7).
 */
export function configUrl(ref: ElementConfigRef, options: LoaderOptions): string {
  const url = new URL(`/${CONFIG_API_VERSION}/elements/${encodeURIComponent(ref.code)}/config`, options.origin);
  if (options.testMode) url.searchParams.set('test', '1');
  return url.toString();
}

const SELECTOR = '[data-nextifycrm], a[href*="#nextifycrm/"]';

function findTargets(root: ParentNode): HTMLElement[] {
  const found: HTMLElement[] = Array.prototype.slice.call(
    root.querySelectorAll<HTMLElement>(SELECTOR),
  );
  // querySelectorAll only searches DESCENDANTS. When a tag manager injects the
  // element itself, that node IS the target and would otherwise be skipped —
  // the exact case the MutationObserver below exists to catch.
  if (root instanceof HTMLElement && root.matches(SELECTOR)) found.unshift(root);
  return found;
}

function hostedCheckoutUrl(ref: ElementConfigRef, options: LoaderOptions): string {
  const url = new URL('/give', options.origin);
  url.searchParams.set('code', ref.code);
  url.searchParams.set('embedded', '1');
  url.searchParams.set('session_id', ref.sessionId);
  const page = new URL(window.location.href);
  if (ref.attributionToken) url.searchParams.set('attribution', ref.attributionToken);
  for (const name of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
    const value = page.searchParams.get(name);
    if (value) url.searchParams.set(name, value);
  }
  const amount = ref.amount ?? page.searchParams.get('nextifycrm_amount');
  const fund = ref.fund ?? page.searchParams.get('fund');
  if (amount) {
    const cents = /^\d+$/.test(amount) ? Number(amount) : Math.round(Number(amount) * 100);
    if (Number.isSafeInteger(cents) && cents > 0) url.searchParams.set('amount_cents', String(cents));
  }
  if (fund) url.searchParams.set('designation', fund);
  if (ref.campaign) url.searchParams.set('campaign', ref.campaign);
  return url.toString();
}

function preconnect(href: string): void {
  if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = href;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

function warmCheckout(ref: ElementConfigRef, options: LoaderOptions): HTMLIFrameElement {
  const src = hostedCheckoutUrl(ref, options);
  const existing = warmed.get(src);
  if (existing) return existing;
  preconnect(options.origin);
  preconnect('https://js.stripe.com');
  const iframe = document.createElement('iframe');
  iframe.src = src;
  iframe.title = `Donation checkout for ${ref.code}`;
  iframe.setAttribute('sandbox', 'allow-scripts allow-forms allow-same-origin allow-popups allow-top-navigation-by-user-activation');
  iframe.setAttribute('allow', 'payment *');
  Object.assign(iframe.style, {
    position: 'fixed', width: '1px', height: '1px', inset: '0 auto auto 0',
    opacity: '0', pointerEvents: 'none', border: '0',
  });
  document.body.appendChild(iframe);
  warmed.set(src, iframe);
  return iframe;
}

function warmOnViewportDwell(target: HTMLElement, warm: () => void, viewed: () => void): void {
  if (typeof IntersectionObserver === 'undefined') return;
  let timer: number | null = null;
  const observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      timer ??= window.setTimeout(() => {
        observer.disconnect();
        warm();
        viewed();
      }, 1_000);
    } else if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  });
  observer.observe(target);
}

function lockHostPage(): void {
  lockedScrollY = window.scrollY;
  const body = document.body;
  savedBodyStyle = {
    position: body.style.position,
    top: body.style.top,
    width: body.style.width,
  };
  body.style.position = 'fixed';
  body.style.top = `-${lockedScrollY}px`;
  body.style.width = '100%';
  inertedByLoader = [];
  for (const child of Array.from(body.children)) {
    if (child !== activeModal && child instanceof HTMLElement && !child.hasAttribute('inert')) {
      child.setAttribute('inert', '');
      inertedByLoader.push(child);
    }
  }
}

function unlockHostPage(): void {
  const body = document.body;
  for (const child of inertedByLoader) child.removeAttribute('inert');
  inertedByLoader = [];
  body.style.position = savedBodyStyle?.position ?? '';
  body.style.top = savedBodyStyle?.top ?? '';
  body.style.width = savedBodyStyle?.width ?? '';
  savedBodyStyle = null;
  window.scrollTo(0, lockedScrollY);
}

function closeModal(fromHistory = false): void {
  if (!activeModal) return;
  if (activeFunnelRef && activeFunnelOptions) {
    reportFunnel(activeFunnelRef, activeFunnelOptions, 'checkout_closed');
  }
  const iframe = activeModal.querySelector('iframe');
  if (iframe) {
    Object.assign((iframe as HTMLIFrameElement).style, {
      position: 'fixed', width: '1px', height: '1px', inset: '0 auto auto 0',
      opacity: '0', pointerEvents: 'none', border: '0',
    });
    document.body.appendChild(iframe);
  }
  activeModal.remove();
  activeModal = null;
  activeCheckoutOrigin = null;
  activeCheckoutWindow = null;
  activeFunnelRef = null;
  activeFunnelOptions = null;
  unlockHostPage();
  activeTrigger?.focus();
  activeTrigger = null;
  if (!fromHistory && modalOwnsHistoryEntry) history.back();
  modalOwnsHistoryEntry = false;
}

function openModal(ref: ElementConfigRef, options: LoaderOptions, trigger: HTMLElement): void {
  if (activeModal) return;
  const iframe = warmCheckout(ref, options);
  const backdrop = document.createElement('div');
  const closeButton = document.createElement('button');
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-label', 'Donation checkout');
  Object.assign(backdrop.style, {
    position: 'fixed', inset: '0', zIndex: '2147483646', display: 'grid',
    placeItems: 'center', padding: '20px', background: 'rgba(12,22,22,.64)',
    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
  });
  Object.assign(iframe.style, {
    position: 'relative', inset: 'auto', width: 'min(928px, 100%)',
    height: 'min(760px, calc(100vh - 40px))', opacity: '1', pointerEvents: 'auto',
    // `--surface`: what the checkout document paints, so the iframe's own
    // backing matches it for the frame before /give's CSS lands.
    border: '0', borderRadius: '20px', background: '#f7f8fa',
    boxShadow: '0 28px 80px rgba(0,0,0,.32)',
  });
  closeButton.type = 'button';
  closeButton.textContent = 'Close';
  closeButton.setAttribute('aria-label', 'Close donation checkout');
  Object.assign(closeButton.style, {
    position: 'fixed', inset: '16px 16px auto auto', zIndex: '2', minHeight: '40px',
    border: '1px solid rgba(255,255,255,.55)', borderRadius: '999px', padding: '0 14px',
    // `--ink`, by ROLE not lineage: this is the Close label on a near-white
    // pill, i.e. body ink. The value it replaces (#182322) matched no token in
    // either palette, before or after the rebrand, so there was no lineage to
    // follow — only the job it does.
    background: 'rgba(255,255,255,.94)', color: '#10172a', cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(0,0,0,.18)', font: '600 13px/1 ui-sans-serif,system-ui,sans-serif',
  });
  closeButton.addEventListener('click', () => closeModal());
  if (window.matchMedia('(max-width: 600px)').matches) {
    /*
      FULL SCREEN on a phone, not a sheet. The old `min(94dvh, 760px)` bottom
      sheet capped at 760px, so on any phone taller than that the form sat in a
      partial panel with dimmed host page above it — 6dvh plus the overflow of a
      tall screen, spent on nothing, while the payment step is the place that
      actually wants the height. A checkout is the task, so it takes the screen.
    */
    Object.assign(backdrop.style, { placeItems: 'stretch', padding: '0' });
    Object.assign(iframe.style, {
      width: '100%', height: '100%', borderRadius: '0', boxShadow: 'none',
    });
    // Clear of the notch/status bar now that nothing is inset from the top.
    Object.assign(closeButton.style, {
      inset: 'calc(env(safe-area-inset-top, 0px) + 10px) 10px auto auto',
    });
  }
  backdrop.appendChild(iframe);
  backdrop.appendChild(closeButton);
  activeModal = backdrop;
  activeTrigger = trigger;
  activeCheckoutOrigin = new URL(options.origin).origin;
  activeFunnelRef = ref;
  activeFunnelOptions = options;
  document.body.appendChild(backdrop);
  /*
    AFTER the append, and it has to be: a detached iframe has a null
    `contentWindow`, so reading it before this line captured null forever. The
    message handler below compares `event.source` against this value, so a null
    here silently discarded EVERY message the checkout sent — `nextifycrm:close`
    (so the checkout could not dismiss its own modal), `nextifycrm:complete` (so
    a partner's completion listener never fired), and it also skipped the
    attribution token we post INTO the checkout, which is guarded on this being
    non-null.
  */
  activeCheckoutWindow = iframe.contentWindow;
  reportFunnel(ref, options, 'checkout_open');
  lockHostPage();
  iframe.focus();
  modalOwnsHistoryEntry = location.hash !== `#nextifycrm/${ref.code}`;
  if (modalOwnsHistoryEntry) history.pushState({ nextifycrm: ref.code }, '', `#nextifycrm/${ref.code}`);
  backdrop.addEventListener('click', (event) => {
    if (event.target === backdrop) closeModal();
  });
}

async function fetchConfig(ref: ElementConfigRef, options: LoaderOptions): Promise<PublicConfig | null> {
  try {
    const response = await fetch(configUrl(ref, options), { mode: 'cors', credentials: 'omit' });
    if (!response.ok) return null;
    return (await response.json()) as PublicConfig;
  } catch (error) {
    warn('could not load element config ' + ref.code, error);
    return null;
  }
}

function buttonStyle(button: HTMLButtonElement, color: string): void {
  Object.assign(button.style, {
    minHeight: '48px', border: '1px solid ' + color, borderRadius: '10px',
    padding: '10px 18px', background: color, color: '#fff', cursor: 'pointer',
    font: '600 15px/1.25 ui-sans-serif,system-ui,sans-serif',
  });
}

function moneyLabel(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(cents / 100);
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** `--accent` fallback when the org has set no brand colour. Single call site for palette-parity. */
function accentColor(config: PublicConfig): string {
  return config.appearance.color || '#1b3fd1';
}

/**
 * Goal meter chrome: raised/goal bar, donor count, and a CTA that opens the
 * same modal checkout as a donate button. Polls config every 60s; animates the
 * fill only when the raised total changes and reduced-motion is off.
 */
function paintGoalMeter(
  host: HTMLElement,
  ref: ElementConfigRef,
  options: LoaderOptions,
): void {
  host.textContent = '';
  Object.assign(host.style, {
    display: 'block', maxWidth: '28rem', minWidth: '0', width: '100%',
    boxSizing: 'border-box', fontFamily: 'ui-sans-serif,system-ui,sans-serif',
  });

  const root = document.createElement('div');
  root.className = 'nextifycrm-el nextifycrm-el--goal-meter';
  root.setAttribute('data-nextifycrm-code', ref.code);
  Object.assign(root.style, {
    display: 'grid', gap: '12px', padding: '16px', borderRadius: '12px',
    // `--surface-raised` mirrored for partner pages without our tokens.
    border: '1px solid rgba(16,23,42,.12)', background: '#ffffff',
    boxSizing: 'border-box', width: '100%',
  });

  const title = document.createElement('p');
  Object.assign(title.style, {
    margin: '0', font: '600 15px/1.35 ui-sans-serif,system-ui,sans-serif',
    // `--ink`
    color: '#10172a',
  });
  title.textContent = 'Campaign progress';

  const amounts = document.createElement('p');
  Object.assign(amounts.style, {
    margin: '0', display: 'flex', flexWrap: 'wrap', gap: '6px 10px',
    alignItems: 'baseline', font: '600 18px/1.2 ui-sans-serif,system-ui,sans-serif',
    color: '#10172a', fontVariantNumeric: 'tabular-nums',
  });
  const raisedEl = document.createElement('bdi');
  raisedEl.textContent = '…';
  const goalEl = document.createElement('span');
  Object.assign(goalEl.style, {
    font: '500 13px/1.3 ui-sans-serif,system-ui,sans-serif',
    // `--ink-label`
    color: '#606b80',
  });
  amounts.appendChild(raisedEl);
  amounts.appendChild(goalEl);

  const track = document.createElement('div');
  track.setAttribute('role', 'progressbar');
  track.setAttribute('aria-valuemin', '0');
  track.setAttribute('aria-valuemax', '100');
  track.setAttribute('aria-valuenow', '0');
  track.setAttribute('aria-label', 'Campaign fundraising progress');
  Object.assign(track.style, {
    // `--surface-sunken`
    height: '10px', borderRadius: '999px', background: '#edeff4', overflow: 'hidden',
  });
  const fill = document.createElement('div');
  Object.assign(fill.style, {
    height: '100%', width: '0%', borderRadius: '999px', background: '#1b3fd1',
    transition: prefersReducedMotion() ? 'none' : 'width 220ms ease-out',
  });
  track.appendChild(fill);

  const meta = document.createElement('p');
  Object.assign(meta.style, {
    margin: '0', font: '500 13px/1.35 ui-sans-serif,system-ui,sans-serif',
    color: '#606b80',
  });
  meta.textContent = 'Loading progress…';

  const cta = document.createElement('button');
  cta.type = 'button';
  cta.textContent = 'Donate';
  buttonStyle(cta, '#1b3fd1');
  Object.assign(cta.style, { width: '100%', minHeight: '44px' });

  const open = (event: Event) => {
    event.preventDefault();
    if (cta.disabled) return;
    openModal(ref, options, cta);
  };
  const warm = () => warmCheckout(ref, options);
  cta.addEventListener('pointerenter', warm, { once: true, passive: true });
  cta.addEventListener('touchstart', warm, { once: true, passive: true });
  cta.addEventListener('click', open);
  warmOnViewportDwell(root, warm, () => reportFunnel(ref, options, 'element_view'));

  root.appendChild(title);
  root.appendChild(amounts);
  root.appendChild(track);
  root.appendChild(meta);
  root.appendChild(cta);
  host.appendChild(root);

  let pollTimer: number | null = null;

  function apply(config: PublicConfig): void {
    if (!host.isConnected) return;
    const color = accentColor(config);
    const progress = config.progress;
    const raised = progress?.raisedCents ?? 0;
    const goal = progress?.goalCents ?? config.campaign?.goalCents ?? null;
    const state = progress?.state ?? 'live';
    const name = progress?.campaignName ?? config.campaign?.name ?? null;
    if (name) title.textContent = name;
    if (config.appearance.label) cta.textContent = config.appearance.label;
    buttonStyle(cta, color);
    Object.assign(cta.style, { width: '100%', minHeight: '44px' });
    fill.style.background = color;

    raisedEl.textContent = moneyLabel(raised);
    if (goal !== null && goal > 0) {
      goalEl.textContent = 'raised of ' + moneyLabel(goal);
      const pct = Math.max(0, Math.min(100, Math.round((raised / goal) * 100)));
      fill.style.width = pct + '%';
      track.setAttribute('aria-valuenow', String(pct));
      track.setAttribute('aria-valuetext', moneyLabel(raised) + ' of ' + moneyLabel(goal));
    } else {
      goalEl.textContent = 'raised';
      fill.style.width = raised > 0 ? '100%' : '0%';
      track.setAttribute('aria-valuenow', raised > 0 ? '100' : '0');
      track.removeAttribute('aria-valuetext');
    }

    const donors = progress?.donorCount ?? 0;
    const donorLine = donors === 1 ? '1 donor' : donors + ' donors';
    if (state === 'closed') {
      meta.textContent = donorLine + ' · Campaign closed';
      cta.disabled = true;
      cta.style.opacity = '0.55';
      cta.style.cursor = 'not-allowed';
    } else if (state === 'goal_met') {
      meta.textContent = donorLine + ' · Goal met — thank you';
      cta.disabled = false;
      cta.style.opacity = '1';
      cta.style.cursor = 'pointer';
    } else {
      meta.textContent = donorLine;
      cta.disabled = false;
      cta.style.opacity = '1';
      cta.style.cursor = 'pointer';
    }
  }

  function refresh(): void {
    void fetchConfig(ref, options).then((config) => {
      if (config) apply(config);
      else meta.textContent = 'Progress unavailable';
    });
  }

  refresh();
  pollTimer = window.setInterval(refresh, 60_000);
  // Clear the poll when the host leaves the document.
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(() => {
      if (!host.isConnected && pollTimer !== null) {
        window.clearInterval(pollTimer);
        pollTimer = null;
        observer.disconnect();
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
}

/**
 * Paint immediate fallback chrome, then enhance it from the public edge config.
 */
function paint(host: HTMLElement, ref: ElementConfigRef, options: LoaderOptions): void {
  if (ref.kind === 'goal-meter') {
    paintGoalMeter(host, ref, options);
    return;
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'nextifycrm-el nextifycrm-el--' + ref.kind;
  button.setAttribute('data-nextifycrm-code', ref.code);
  button.textContent = host.textContent?.trim() || 'Donate';
  // `--accent`, the fallback chrome painted before the edge config arrives.
  buttonStyle(button, '#1b3fd1');

  const open = (event: Event) => {
    event.preventDefault();
    const trigger = event.currentTarget;
    if (trigger instanceof HTMLElement) openModal(ref, options, trigger);
  };

  const warm = () => warmCheckout(ref, options);

  if (ref.kind === 'link' || host.tagName === 'A') {
    // A Donate Link is the host page's own anchor: attach behaviour, change
    // nothing about the markup. Constraint 4.
    host.addEventListener('pointerenter', warm, { once: true, passive: true });
    host.addEventListener('touchstart', warm, { once: true, passive: true });
    host.addEventListener('click', open);
    warmOnViewportDwell(host, warm, () => reportFunnel(ref, options, 'element_view'));
    return;
  }

  button.addEventListener('pointerenter', warm, { once: true, passive: true });
  button.addEventListener('touchstart', warm, { once: true, passive: true });
  button.addEventListener('click', open);
  warmOnViewportDwell(button, warm, () => reportFunnel(ref, options, 'element_view'));
  host.textContent = '';
  host.appendChild(button);

  void fetchConfig(ref, options).then((config) => {
    if (!config || !host.isConnected) return;
    // `--accent` again: the default for an org that has set no brand colour.
    const color = accentColor(config);
    buttonStyle(button, color);
    if (config.appearance.label && ref.kind === 'button') button.textContent = config.appearance.label;
    if (ref.kind !== 'preset-amounts') return;
    const selected = config.funds.find((fund) => fund.id === ref.fund) ??
      config.funds.find((fund) => fund.id === config.defaultFundId) ?? config.funds[0];
    const presets = selected?.zakatMode ? config.presets.zakat : config.presets.default;
    host.textContent = '';
    Object.assign(host.style, { display: 'flex', flexWrap: 'wrap', gap: '8px' });
    for (const cents of presets.slice(0, 5)) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.textContent = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(cents / 100);
      Object.assign(chip.style, {
        minHeight: '44px', border: '1px solid ' + color, borderRadius: '999px',
        padding: '8px 14px', background: '#fff', color, cursor: 'pointer',
        font: '600 14px/1 ui-sans-serif,system-ui,sans-serif',
      });
      chip.addEventListener('pointerenter', warm, { once: true, passive: true });
      chip.addEventListener('touchstart', warm, { once: true, passive: true });
      chip.addEventListener('click', (event) => {
        event.preventDefault();
        openModal({ ...ref, amount: String(cents) }, options, chip);
      });
      host.appendChild(chip);
    }
  });
}

function mountOne(el: HTMLElement, options: LoaderOptions): boolean {
  if (mountedHosts.has(el)) return false;
  const ref = parseElement(el);
  if (!ref) {
    warn(
      'element has no code; expected data-nextifycrm="CODE" or href="#nextifycrm/CODE"',
      el,
    );
    return false;
  }
  mountedHosts.add(el);
  try {
    paint(el, ref, options);
  } catch (error) {
    warn('failed to render element ' + ref.code, error);
    return false;
  }
  if (options.debug) console.log('[nextifycrm] mounted', ref.code, configUrl(ref, options));
  return true;
}

/** Mount every unmounted element under `root`. Safe to call repeatedly. */
export function mount(root: ParentNode = document): number {
  const options = readOptions();
  let mounted = 0;
  for (const el of findTargets(root)) if (mountOne(el, options)) mounted++;
  return mounted;
}

function boot(): void {
  const options = readOptions();
  mount(document);

  // Partner sites are full of tag managers, A/B tools and SPA routers that
  // inject markup after load. Without this, elements added later never mount.
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of Array.prototype.slice.call(record.addedNodes)) {
          if (node instanceof HTMLElement) mount(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (options.debug) console.log('[nextifycrm] loader ready', options);
}

window.addEventListener('message', (event) => {
  if (event.origin !== activeCheckoutOrigin || event.source !== activeCheckoutWindow) return;
  if (event.data?.type === 'nextifycrm:close') closeModal();
  if (event.data?.type === 'nextifycrm:complete') {
    window.dispatchEvent(new CustomEvent('nextifycrm:complete', { detail: event.data }));
  }
});
window.addEventListener('hashchange', () => {
  if (activeModal && !location.hash.startsWith('#nextifycrm/')) closeModal(true);
});
window.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && activeModal) closeModal();
});

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
